import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Megaphone, Filter, Users, FileText, TrendingUp, ArrowRight, MessageCircle } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const Stat = ({ icon: Icon, label, value, accent }) => (
  <div className="rounded-2xl border border-white/10 bg-[#121212] p-6">
    <Icon className={accent ? "text-yellow-400" : "text-purple-500"} size={24} />
    <div className="font-display font-black text-3xl mt-4">{value}</div>
    <div className="text-sm text-zinc-500 mt-1">{label}</div>
  </div>
);

export default function Overview() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const profileIncomplete = !user?.company_name;
  const whatsappMissing = !user?.whatsapp;

  return (
    <div data-testid="overview-page">
      <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl">
        Hola, {user?.name?.split(" ")[0] || "Líder"} 👋
      </h1>
      <p className="text-zinc-400 mt-2">Tu centro de mando para dominar el multinivel.</p>

      {whatsappMissing && (
        <div data-testid="whatsapp-missing-banner" className="mt-6 rounded-2xl border border-green-500/40 bg-green-500/5 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <MessageCircle className="text-green-400 mt-0.5 shrink-0" size={22} />
            <div>
              <div className="font-semibold text-green-400">Configura tu WhatsApp para recibir prospectos</div>
              <p className="text-sm text-zinc-400">Es tu número individual a donde llegarán tus prospectos calificados. Sin él, no podrás publicar campañas.</p>
            </div>
          </div>
          <button
            data-testid="config-whatsapp-cta"
            onClick={() => navigate("/dashboard/perfil")}
            className="bg-green-500 hover:bg-green-400 text-black font-bold rounded-full px-5 py-2.5 text-sm whitespace-nowrap"
          >
            Configurar WhatsApp
          </button>
        </div>
      )}

      {profileIncomplete && (
        <div className="mt-6 rounded-2xl border border-yellow-400/40 bg-yellow-400/5 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="font-semibold text-yellow-impact">Completa tu Perfil de Empresa</div>
            <p className="text-sm text-zinc-400">La IA generará mejor contenido cuando sepa de tu empresa, productos y plan.</p>
          </div>
          <button
            data-testid="complete-profile-cta"
            onClick={() => navigate("/dashboard/perfil")}
            className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-full px-5 py-2.5 text-sm whitespace-nowrap"
          >
            Completar ahora
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
        <Stat icon={Sparkles} label="Conceptos IA" value={stats?.concepts ?? "-"} />
        <Stat icon={FileText} label="Contenidos" value={stats?.contents ?? "-"} />
        <Stat icon={Megaphone} label="Campañas" value={stats?.campaigns ?? "-"} />
        <Stat icon={Filter} label="Embudos" value={stats?.funnels ?? "-"} />
        <Stat icon={Users} label="Prospectos" value={stats?.leads ?? "-"} />
        <Stat icon={TrendingUp} label="Calificados" value={stats?.qualified_leads ?? "-"} accent />
      </div>

      <div className="grid sm:grid-cols-2 gap-5 mt-8">
        <button
          data-testid="quick-generator"
          onClick={() => navigate("/dashboard/generador")}
          className="text-left rounded-2xl border border-white/10 bg-gradient-to-br from-purple-600/20 to-transparent p-7 hover:border-purple-500/60 hover:-translate-y-1 transition-all"
        >
          <Sparkles className="text-purple-400 mb-3" size={26} />
          <h3 className="font-display font-bold text-xl">Crear con IA</h3>
          <p className="text-zinc-400 text-sm mt-1">Convierte una idea en una campaña completa.</p>
          <span className="inline-flex items-center gap-1 text-purple-400 text-sm font-semibold mt-4">Empezar <ArrowRight size={16} /></span>
        </button>
        <button
          data-testid="quick-funnel"
          onClick={() => navigate("/dashboard/embudos")}
          className="text-left rounded-2xl border border-white/10 bg-[#121212] p-7 hover:border-purple-500/60 hover:-translate-y-1 transition-all"
        >
          <Filter className="text-yellow-400 mb-3" size={26} />
          <h3 className="font-display font-bold text-xl">Crear un Embudo</h3>
          <p className="text-zinc-400 text-sm mt-1">Captura prospectos calificados en automático.</p>
          <span className="inline-flex items-center gap-1 text-purple-400 text-sm font-semibold mt-4">Crear <ArrowRight size={16} /></span>
        </button>
      </div>
    </div>
  );
}
